import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter } from 'react-router-dom';
import { App } from '@/App';
import { AppBootstrap } from '@/components/AppBootstrap';
import { buildOidcConfig } from '@/lib/auth';
import { loadRuntimeConfig } from '@/runtimeConfig';
import '@/styles/design-system.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5_000,
    },
  },
});

function mount(): HTMLElement {
  const root = document.getElementById('root');
  if (!root) {
    throw new Error('index.html is missing the #root element.');
  }
  return root;
}

// Configuration must resolve before anything renders: the OIDC config is derived from it,
// and building that at module-import time is the most common way this pattern breaks.
async function bootstrap(): Promise<void> {
  const root = mount();

  await loadRuntimeConfig();

  const oidcConfig = buildOidcConfig();

  createRoot(root).render(
    <StrictMode>
      <AuthProvider {...oidcConfig}>
        <QueryClientProvider client={queryClient}>
          <AppBootstrap>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AppBootstrap>
        </QueryClientProvider>
      </AuthProvider>
    </StrictMode>,
  );
}

bootstrap().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  const root = document.getElementById('root');

  if (root) {
    root.innerHTML = '';
    const alert = document.createElement('div');
    alert.setAttribute('role', 'alert');
    alert.style.cssText =
      'min-height:100vh;display:grid;place-items:center;padding:24px;font-family:system-ui,sans-serif';
    alert.innerHTML =
      '<div style="max-width:520px"><h1 style="font-size:18px;margin:0 0 8px">Configuration error</h1>' +
      '<p style="margin:0 0 12px;color:#54545f">Sturm could not load its runtime configuration and cannot start.</p>' +
      '<pre style="background:#f3f3f1;padding:12px;border-radius:8px;overflow:auto;font-size:12px"></pre></div>';
    const pre = alert.querySelector('pre');
    if (pre) {
      pre.textContent = message;
    }
    root.appendChild(alert);
  }
});
